import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Badge,
  Betslip,
  Button,
  DesktopEventRow,
  EmptyBetslip,
  EmptyState,
  EventRow,
  Icon,
  OddsButton,
  Skeleton,
  SkeletonText,
  Spinner,
  Toast,
  type OddsFlash,
} from '@/ui';

/**
 * Living reference for the design-system primitives. It doubles as the visual
 * check that every token resolves in all eight scheme/theme combinations.
 */
export const Route = createFileRoute('/ui-kit')({
  component: UiKit,
});

function UiKit() {
  const [selected, setSelected] = useState<string | null>('1');
  const [flash, setFlash] = useState<OddsFlash>('none');

  return (
    <div className="flex flex-col gap-8 pb-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-heading-l text-text-primary">UI kit</h1>
        <p className="text-body-m text-text-secondary">
          Switch scheme and theme in the header to check every mode.
        </p>
      </header>

      <Section title="Button">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary">Place bet</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Cancel bet</Button>
          <Button disabled>Disabled</Button>
          <Button loading>Placing</Button>
          <Button icon="search" variant="secondary">
            With icon
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </div>
      </Section>

      <Section title="OddsButton">
        <div className="flex flex-wrap items-center gap-2">
          {['1', 'X', '2'].map((outcome) => (
            <OddsButton
              key={outcome}
              outcome={outcome}
              odds="1.95"
              state={selected === outcome ? 'selected' : 'default'}
              flash={selected === outcome ? flash : 'none'}
              onClick={() => setSelected(outcome)}
              className="max-w-32"
            />
          ))}
          <OddsButton outcome="1" odds="2.40" state="suspended" className="max-w-32" />
          <OddsButton outcome="1" odds="2.40" state="locked" className="max-w-32" />
        </div>
        <div className="flex gap-2">
          {(['none', 'up', 'down'] as const).map((value) => (
            <Button key={value} size="sm" variant="secondary" onClick={() => setFlash(value)}>
              Flash {value}
            </Button>
          ))}
        </div>
      </Section>

      <Section title="Badge">
        <div className="flex flex-wrap items-center gap-2">
          {(['neutral', 'success', 'warning', 'danger', 'info', 'live'] as const).map((tone) => (
            <Badge key={tone} tone={tone}>
              {tone}
            </Badge>
          ))}
        </div>
      </Section>

      <Section title="EventRow">
        <div className="grid gap-3 lg:grid-cols-2">
          <EventRow
            league="Denmark > Superliga"
            kickoff="Today, 16:00"
            competitors={[{ name: 'FC Copenhagen' }, { name: 'Lyngby BK' }]}
            marketCode="1x2"
            additionalMarkets={891}
            outcomes={[
              { id: '1', outcome: '1', odds: '1.55' },
              { id: 'X', outcome: 'X', odds: '3.80', flash: 'up' },
              { id: '2', outcome: '2', odds: '5.25' },
            ]}
          />
          <EventRow
            league="Denmark > Superliga"
            kickoff="Today, 16:00"
            state="suspended"
            competitors={[{ name: 'FC Copenhagen' }, { name: 'Lyngby BK' }]}
            marketCode="1x2"
            additionalMarkets={891}
            outcomes={[
              { id: '1', outcome: '1', odds: '1.55' },
              { id: 'X', outcome: 'X', odds: '3.80' },
              { id: '2', outcome: '2', odds: '5.25' },
            ]}
          />
          <EventRow
            type="live"
            league="Germany > Bundesliga"
            clock="67' · 2HT"
            indicators={['livestream']}
            competitors={[
              { name: 'Jeju SK FC', score: '2' },
              { name: 'Bayern Munich', score: '1' },
            ]}
            marketCode="1x2"
            additionalMarkets={124}
            outcomes={[
              { id: '1', outcome: '1', odds: '2.10', state: 'selected' },
              { id: 'X', outcome: 'X', odds: '3.10' },
              { id: '2', outcome: '2', odds: '2.95', flash: 'down' },
            ]}
          />
          <EventRow
            type="live"
            league="USA > NBA"
            clock="08:22 · 2Q"
            competitors={[
              { name: 'Boston Celtics', score: '54', periodScores: '22 30' },
              { name: 'LA Lakers', score: '49', periodScores: '1 2' },
            ]}
            marketCode="1x2"
            additionalMarkets={210}
            outcomes={[
              { id: '1', outcome: '1', odds: '1.85' },
              { id: 'X', outcome: 'X', odds: '12.0' },
              { id: '2', outcome: '2', odds: '2.05' },
            ]}
          />
        </div>
      </Section>

      <Section title="DesktopEventRow">
        <div className="flex flex-col gap-2">
          <DesktopEventRow
            league="England · Premier League"
            kickoffDate="10/08"
            kickoffTime="21:15"
            competitors={[{ name: 'Arsenal' }, { name: 'Chelsea' }]}
            additionalMarkets={188}
            markets={[
              {
                code: '1x2',
                outcomes: [
                  { id: '1', outcome: '1', odds: '1.70' },
                  { id: 'X', outcome: 'X', odds: '3.65' },
                  { id: '2', outcome: '2', odds: '4.90' },
                ],
              },
              {
                code: 'Total',
                outcomes: [
                  { id: 'over', outcome: 'Over 2.5', odds: '1.85' },
                  { id: 'under', outcome: 'Under 2.5', odds: '1.95' },
                ],
              },
              {
                code: 'Double chance',
                outcomes: [
                  { id: '1X', outcome: '1X', odds: '1.14' },
                  { id: '12', outcome: '12', odds: '1.23' },
                  { id: 'X2', outcome: 'X2', odds: '2.09' },
                ],
              },
            ]}
          />
          <DesktopEventRow
            type="live"
            league="Germany · Bundesliga"
            clock="67' · 2HT"
            indicators={['livestream']}
            competitors={[
              { name: 'Bayern Munich', score: '2' },
              { name: 'Dortmund', score: '1' },
            ]}
            additionalMarkets={124}
            markets={[
              {
                code: '1x2',
                outcomes: [
                  { id: '1', outcome: '1', odds: '2.10', state: 'selected' },
                  { id: 'X', outcome: 'X', odds: '3.10' },
                  { id: '2', outcome: '2', odds: '2.95', flash: 'down' },
                ],
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Betslip">
        <div className="grid gap-3 lg:grid-cols-2">
          <EmptyBetslip className="rounded-md border border-border-subtle bg-bg-surface p-3" />
          <Betslip className="h-80" />
          <Betslip
            className="h-80"
            selectionCount={2}
            totals={[
              { label: 'Total odds', value: '4.81' },
              { label: 'Stake', value: '50.00 ₺' },
              { label: 'Potential payout', value: '240.50 ₺', emphasis: true },
            ]}
          >
            <div className="rounded-md border border-border-subtle bg-bg-elevated p-3 text-body-s text-text-secondary">
              FC Copenhagen vs Lyngby BK — 1 @ 1.55
            </div>
            <div className="rounded-md border border-border-subtle bg-bg-elevated p-3 text-body-s text-text-secondary">
              Jeju SK FC vs Bayern Munich — 1 @ 3.10
            </div>
          </Betslip>
        </div>
      </Section>

      <Section title="Feedback">
        <div className="flex flex-wrap items-center gap-4">
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" />
          <Skeleton kind="line" />
          <Skeleton kind="block" />
          <Skeleton kind="circle" />
        </div>
        <SkeletonText className="max-w-md" />
        <div className="flex flex-col gap-2">
          {(['info', 'success', 'warning', 'danger'] as const).map((tone) => (
            <Toast key={tone} tone={tone} onDismiss={() => undefined}>
              Bet placed successfully — {tone}
            </Toast>
          ))}
        </div>
        <EmptyState
          icon="betslip-empty"
          title="No events found"
          description="Try a different sport or clear your filters."
          action={<Button variant="secondary">Clear filters</Button>}
        />
      </Section>

      <Section title="Icons">
        <div className="flex flex-wrap gap-3 text-icon-default">
          {(
            [
              'search',
              'betslip',
              'live-icon',
              'mytickets',
              'casino',
              'bonuses',
              'settings',
              'user',
              'deposit',
              'withdrawal',
              'stats',
              'star',
              'lock',
            ] as const
          ).map((name) => (
            <span
              key={name}
              className="flex size-10 items-center justify-center rounded-md bg-bg-surface"
              title={name}
            >
              <Icon name={name} size={20} />
            </span>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-heading-s text-text-primary">{title}</h2>
      {children}
    </section>
  );
}
